<script lang="ts">
  /**
   * Shared catalog list for HF / Cloudflare / CivitAI.
   * Connect writes a catalog pref. No rent, no hardware pick.
   */
  import { onMount } from "svelte";
  import type { GpuPluginHost } from "../host.js";
  import {
    hostAllowsConnect,
    hostAllowsCivitaiRed,
    hostAllowsHfLora,
    hostListedModalities,
    modalityFamily,
    toast,
  } from "../host.js";
  import {
    DEFAULT_PAIR_PROMPT,
    fetchCivitaiCatalog,
    fetchCloudflareCatalog,
    fetchHfCatalog,
    fetchProductionCatalog,
    fetchHubProviderModels,
    modelHasImageInput,
    probeCatalogChat,
    type ForgeClientOptions,
  } from "../client.js";
  import type {
    CatalogSortMode,
    InferenceCatalogResponse,
    InferenceModel,
    ModalityFilter,
  } from "../types.js";
  import {
    canAccessInferenceModel,
    canAccessModel,
    freeModelBandLabel,
    creditsBandLabel,
  } from "../access.js";
  import { chapterEstUsd } from "../cost.js";
  import { createFavoritesStore } from "../favorites.js";
  import { formatUsd, uniqueBy } from "../utils.js";
  import { connectCatalogModel, connectProductionModel } from "../connect.js";
  import { fetchSuiteConnections } from "../suite-client.js";
  import {
    catalogConnectionId,
    catalogRowKey,
    engineName,
  } from "../connections.js";
  import {
    addLoraToStack,
    filterByResourceKind,
    isLoraKind,
    loraKey,
    loraMatchesEngine,
    MAX_LORAS,
    modelResourceKind,
    modelToSelectedLora,
    removeLoraFromStack,
    resourceKindLabel,
    setLoraStrength,
    civitaiTypesQuery,
    type ResourceKindFilter,
  } from "../lora.js";

  interface Props {
    host: GpuPluginHost;
    tab: "hf" | "cloudflare" | "civitai" | "production";
  }

  let { host, tab }: Props = $props();

  const META: Record<
    Props["tab"],
    { title: string; subtitle: string; tone: string; noteKey: string }
  > = {
    hf: {
      title: "HF models",
      subtitle: "Hugging Face Inference",
      tone: "amber",
      noteKey: "forgesuite.hf.catalogNote.expanded.v1",
    },
    cloudflare: {
      title: "Cloudflare",
      subtitle: "Cloudflare Workers AI",
      tone: "sky",
      noteKey: "forgesuite.cloudflare.catalogNote.expanded.v1",
    },
    civitai: {
      title: "CivitAI",
      subtitle: "Image and video models",
      tone: "amber",
      noteKey: "forgesuite.civitai.catalogNote.expanded.v1",
    },
    production: {
      title: "Production",
      subtitle: "Models assigned to live tools",
      tone: "sky",
      noteKey: "forgesuite.production.catalogNote.expanded.v1",
    },
  };

  const meta = $derived(META[tab]);

  let models = $state<InferenceModel[]>([]);
  let total = $state(0);
  let byModality = $state<Record<string, number>>({});
  let catalogNote = $state("");
  let configured = $state(false);
  let loadError = $state<string | null>(null);
  let loading = $state(true);
  let q = $state("");
  let modality = $state<ModalityFilter>("all");
  let sortMode = $state<CatalogSortMode>(tab === "hf" ? "name" : "price-asc");
  let favoritesOnly = $state(false);
  let showGreen = $state(true);
  let showRed = $state(false);
  let resourceKindFilter = $state<ResourceKindFilter>(
    host.purpose === "image" || host.purpose === "video" || host.purpose === "vision"
      ? "checkpoint"
      : "all",
  );
  let localLoraStack = $state([...(host.loraStack || [])]);
  const loraStack = $derived(localLoraStack);
  const loraCapable = $derived(Boolean(host.loraCapable));
  const allowRed = $derived(
    (tab === "civitai" || tab === "production") && hostAllowsCivitaiRed(host),
  );
  const showLoraUi = $derived(tab === "civitai" || (tab === "hf" && hostAllowsHfLora(host)));
  $effect(() => {
    if (!allowRed && showRed) showRed = false;
  });
  let selectedSkuId = $state<string | null>(null);
  let connectedId = $state<string | null>(null);
  let probePrompt = $state(DEFAULT_PAIR_PROMPT);
  let probing = $state(false);
  let probeResult = $state<{ ok: boolean; text: string } | null>(null);
  let favIds = $state<string[]>([]);
  let catalogNoteOpen = $state(false);
  let favStore = $state(createFavoritesStore("forgesuite.modelFavorites.v1"));
  let connecting = $state(false);
  let hostedIds = $state<Set<string>>(new Set());
  let civitaiCursors = $state<string[]>([]);
  let civitaiNext = $state<string | null>(null);
  let civitaiSearchPrimed = false;
  const civitaiPage = $derived(civitaiCursors.length + 1);

  const civitaiOffPurpose = $derived(
    tab === "civitai" &&
      host.purpose !== "image" &&
      host.purpose !== "video" &&
      host.purpose !== "vision",
  );

  function client(): ForgeClientOptions {
    return {
      forgeApiUrl: host.forgeApiUrl,
      getAccessToken: host.getAccessToken,
      appId: host.appId,
      toolId: host.accessHubTool,
      dumpCatalogUrl: host.dumpCatalogUrl,
    };
  }

  async function loadCatalog(force = false) {
    loading = true;
    loadError = null;
    try {
      const fetchTab =
        tab === "hf"
          ? fetchHfCatalog
          : tab === "civitai"
            ? fetchCivitaiCatalog
            : tab === "production"
              ? fetchProductionCatalog
              : fetchCloudflareCatalog;
      const data: InferenceCatalogResponse = await fetchTab(client(), {
        modality: "all",
        force,
        app: host.appId,
        tool: host.accessHubTool,
        ...(tab === "civitai"
          ? {
              browse: true,
              cursor: civitaiCursors[civitaiCursors.length - 1],
              q: q.trim() || undefined,
              types: civitaiTypesQuery(resourceKindFilter),
            }
          : {}),
      });
      civitaiNext = tab === "civitai" ? data.nextCursor || null : null;
      if (data.error && !data.models?.length) {
        loadError = data.error;
      }
      configured = Boolean(data.configured);
      total = data.total ?? data.models?.length ?? 0;
      byModality = data.byModality || {};
      catalogNote = data.note || "";
      const raw = data.models || [];
      models = host.excludeCatalogModel
        ? raw.filter(
            (m) =>
              !host.excludeCatalogModel!(m.modelId || m.skuId, {
                name: m.name,
                modality: m.modality,
              }),
          )
        : raw;
      const listed = hostListedModalities(host);
      if (tab === "hf" && listed !== "all") {
        const families = new Set(listed.map((m) => modalityFamily(m)));
        const have = models.some((m) =>
          families.has(modalityFamily(m.modality)),
        );
        if (!have) {
          const extra = await fetchHubProviderModels(listed);
          if (extra.length) {
            models = [...models, ...extra];
          }
        }
      }
      if (models.length !== raw.length) {
        const nextBy: Record<string, number> = {};
        for (const m of models) {
          const k = (m.modality || "other").toLowerCase();
          nextBy[k] = (nextBy[k] || 0) + 1;
        }
        byModality = nextBy;
        total = models.length;
      }
      try {
        const prefs = await fetchSuiteConnections(client(), {
          app: host.appId,
          force: true,
        });
        connectedId = host.purpose
          ? prefs.selectedByPurpose?.[host.purpose] || null
          : null;
        if (tab === "civitai") {
          hostedIds = new Set(
            (prefs.connections || [])
              .filter((c) => c.kind === "forge_endpoint")
              .map((c) => (c.modelId || "").toLowerCase())
              .filter(Boolean),
          );
        }
      } catch {
        if (tab === "civitai") hostedIds = new Set();
      }
      if (listed === "all") {
        const want = modalityBucket(
          (host.defaultInferenceModality || "all") as string,
        );
        const n =
          want === "all"
            ? models.length
            : models.filter((m) => modalityBucket(m.modality || "other") === want)
                .length;
        modality = n === 0 && models.length ? "all" : want;
      } else {
        modality = (host.defaultInferenceModality ||
          listed[0] ||
          "all") as ModalityFilter;
      }
    } catch (e) {
      loadError = e instanceof Error ? e.message : String(e);
      models = [];
      if (tab === "civitai") civitaiNext = null;
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    const listed = hostListedModalities(host);
    if (listed !== "all") {
      modality = (host.defaultInferenceModality || listed[0]) as ModalityFilter;
    } else if (host.defaultInferenceModality) {
      modality = host.defaultInferenceModality;
    } else if (tab === "civitai") {
      modality = "text-to-image";
    }
    if (tab === "civitai") {
      sortMode = "downloads";
    }
    try {
      const g = localStorage.getItem("forgesuite.civitai.showGreen.v1");
      if (g === "0") showGreen = false;
      if (hostAllowsCivitaiRed(host)) {
        const r = localStorage.getItem("forgesuite.civitai.showRed.v1");
        showRed = r !== "0";
      } else {
        showRed = false;
      }
    } catch {
      showRed = false;
    }
    favStore = createFavoritesStore(
      host.favoritesStorageKey ?? "forgesuite.modelFavorites.v1",
    );
    const unsub = favStore.subscribe((ids) => {
      favIds = ids;
    });
    try {
      catalogNoteOpen = localStorage.getItem(meta.noteKey) === "1";
    } catch {
      catalogNoteOpen = false;
    }
    void host.onRefreshWallet?.();
    void loadCatalog();
    return unsub;
  });

  $effect(() => {
    if (tab !== "civitai") return;
    const needle = q;
    void needle;
    if (!civitaiSearchPrimed) {
      civitaiSearchPrimed = true;
      return;
    }
    const t = setTimeout(() => {
      civitaiCursors = [];
      void loadCatalog();
    }, 400);
    return () => clearTimeout(t);
  });

  function civitaiPrev() {
    if (!civitaiCursors.length || loading) return;
    civitaiCursors = civitaiCursors.slice(0, -1);
    void loadCatalog();
  }

  function civitaiNextPage() {
    if (!civitaiNext || loading) return;
    civitaiCursors = [...civitaiCursors, civitaiNext];
    void loadCatalog();
  }

  function toggleCatalogNote() {
    catalogNoteOpen = !catalogNoteOpen;
    try {
      localStorage.setItem(meta.noteKey, catalogNoteOpen ? "1" : "0");
    } catch {
      /* ignore */
    }
  }

  function toggleStar(m: InferenceModel, e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    favStore.toggle(catalogRowKey(m));
  }

  const workUnits = $derived(host.workUnits ?? []);
  const product = $derived(host.productName || "this app");

  const purposeTotal = $derived.by(() => {
    const listed = hostListedModalities(host);
    if (listed === "all") return total || models.length;
    const families = new Set(listed.map((m) => modalityFamily(m)));
    return models.filter((m) => families.has(modalityFamily(m.modality))).length;
  });

  const ALL_MODALITY_TABS: { id: ModalityFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "text-generation", label: "Chat / rewrite" },
    { id: "text-to-speech", label: "TTS" },
    { id: "text-to-image", label: "Image" },
    { id: "text-to-video", label: "Video" },
    { id: "other", label: "Other" },
  ];

  const modalityTabs = $derived.by(() => {
    const listed = hostListedModalities(host);
    if (listed === "all") return ALL_MODALITY_TABS;
    const families = new Set(listed.map((m) => modalityFamily(m)));
    const chips = ALL_MODALITY_TABS.filter(
      (t) => t.id !== "all" && families.has(modalityFamily(t.id)),
    );
    return chips.length > 1 ? chips : [];
  });

  function modalityBucket(mod: string): ModalityFilter {
    if (mod === "text-generation") return "text-generation";
    if (mod === "text-to-speech") return "text-to-speech";
    if (mod === "text-to-image" || mod === "image-to-image") return "text-to-image";
    if (mod === "text-to-video" || mod === "image-to-video") return "text-to-video";
    return "other";
  }

  const filtered = $derived.by(() => {
    const favs = new Set(favIds);
    const listed = hostListedModalities(host);
    let list = models.map((m) => {
      const key = catalogRowKey(m);
      return {
        ...m,
        favorite: favs.has(key) || favs.has(m.modelId),
        bucket: modalityBucket(m.modality || "other"),
      };
    });

    if (listed !== "all") {
      const families = new Set(listed.map((m) => modalityFamily(m)));
      list = list.filter((m) => families.has(modalityFamily(m.modality)));
    }
    if (modality !== "all") {
      list = list.filter((m) => m.bucket === modality);
    }
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((m) => {
        const hay = `${m.name} ${m.modelId} ${m.provider} ${m.modality} ${m.source_tab || ""} ${m.creator || ""} ${m.civitaiType || ""} ${m.baseModel || ""}`.toLowerCase();
        return needle.split(/\s+/).every((t) => hay.includes(t));
      });
    }
    if (favoritesOnly) list = list.filter((m) => m.favorite);
    if (tab === "civitai") {
      list = list.filter((m) => {
        const red = m.rating === "red" || m.nsfw === true;
        if (red && !allowRed) return false;
        return red ? showRed : showGreen;
      });
    }
    if (tab === "production") {
      list = list.filter((m) => {
        const red = m.rating === "red" || m.nsfw === true;
        return !(red && !allowRed);
      });
    }
    if (showLoraUi) {
      list = filterByResourceKind(list, resourceKindFilter);
    }

    list = [...list].sort((a, b) => {
      if (sortMode === "favorites") {
        if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
        return (b.downloadCount || 0) - (a.downloadCount || 0);
      }
      if (sortMode === "name") {
        const byModel = a.name.localeCompare(b.name) || a.modelId.localeCompare(b.modelId);
        if (byModel) return byModel;
        return engineName(a.provider, tab).localeCompare(engineName(b.provider, tab));
      }
      if (sortMode === "downloads")
        return (b.downloadCount || 0) - (a.downloadCount || 0);
      if (sortMode === "price-desc")
        return (b.offer?.pricePerHour || 0) - (a.offer?.pricePerHour || 0);
      return (a.offer?.pricePerHour || 0) - (b.offer?.pricePerHour || 0);
    });
    return uniqueBy(list, (m) => catalogRowKey(m));
  });

  const selected = $derived(
    filtered.find((m) => catalogRowKey(m) === selectedSkuId) ?? filtered[0] ?? null,
  );

  function pairId(m: InferenceModel): string {
    return catalogConnectionId(tab, m.provider || tab, m.modelId);
  }

  function pairChatId(m: InferenceModel): string {
    return (m.chatModelId || m.modelId || "").trim();
  }

  const selectedEngine = $derived(selected ? engineName(selected.provider, tab) : "");
  const selectedIsConnected = $derived(
    Boolean(selected && connectedId && pairId(selected) === connectedId),
  );

  function accessFor(m: InferenceModel) {
    if (tab === "cloudflare") {
      return canAccessInferenceModel({
        paramsB: m.paramsB,
        modality: m.modality,
        isMember: host.wallet.isMember,
        balanceUsd: host.wallet.balanceUsd,
        memberPriceUsd: host.wallet.memberPriceUsd,
      });
    }
    return canAccessModel({
      paramsB: m.paramsB,
      isMember: host.wallet.isMember,
      balanceUsd: host.wallet.balanceUsd,
      memberPriceUsd: host.wallet.memberPriceUsd,
      provider: m.provider,
      tags: m.tags,
      offerId: m.offer?.id,
      modelId: m.modelId,
    });
  }

  const selectedAccess = $derived.by(() => (selected ? accessFor(selected) : null));

  $effect(() => {
    if (!selected) return;
    if (selectedSkuId && filtered.some((m) => catalogRowKey(m) === selectedSkuId)) return;
    selectedSkuId = filtered[0] ? catalogRowKey(filtered[0]) : null;
    probeResult = null;
  });

  function accessBadgeFor(m: InferenceModel): "Member" | "Credits" | null {
    const a = accessFor(m);
    if (a.allowed) return null;
    return a.badge;
  }

  function canConnectSelected(m: InferenceModel | null): boolean {
    if (!m) return false;
    if (civitaiOffPurpose) return false;
    return hostAllowsConnect(host, m.modality);
  }

  function civitaiHosted(m: InferenceModel): boolean {
    const id = (m.modelId || "").toLowerCase();
    const name = (m.name || "").toLowerCase();
    for (const h of hostedIds) {
      if (h === id || h.includes(id) || id.includes(h) || h.includes(name)) return true;
    }
    return false;
  }

  const ctaLabel = $derived(
    tab === "hf"
      ? host.hfCtaLabel || host.connectCtaLabel || "Connect"
      : tab === "civitai"
        ? host.civitaiCtaLabel || host.connectCtaLabel || "Connect"
        : host.cloudflareCtaLabel || host.connectCtaLabel || "Connect",
  );

  function selectedIsLora(m: InferenceModel | null): boolean {
    if (!m || !showLoraUi) return false;
    return isLoraKind(modelResourceKind(m));
  }

  function addSelectedLora() {
    const m = selected;
    if (!m) {
      toast(host, "error", "Nothing selected");
      return;
    }
    if (!loraCapable) {
      toast(
        host,
        "info",
        tab === "hf"
          ? "LoRAs need a Hugging Face checkpoint as the engine, then Add LoRA."
          : "LoRAs need a CivitAI checkpoint as the engine, then Add LoRA.",
      );
      return;
    }
    const next = modelToSelectedLora(m);
    if (!next) {
      toast(host, "error", "This LoRA has no published version to attach.");
      return;
    }
    const result = addLoraToStack(loraStack, next);
    if (!result.ok) {
      toast(host, "info", result.error);
      return;
    }
    if (!result.added) {
      toast(host, "info", "Already in the LoRA stack.");
      return;
    }
    localLoraStack = result.stack;
    host.onLorasChange?.(result.stack);
    toast(host, "success", `LoRA · ${next.name}`);
  }

  function dropLora(key: string) {
    const next = removeLoraFromStack(localLoraStack, key);
    localLoraStack = next;
    host.onLorasChange?.(next);
  }

  function weightLora(key: string, strength: number) {
    const next = setLoraStrength(localLoraStack, key, strength);
    localLoraStack = next;
    host.onLorasChange?.(next);
  }

  const TYPE_CHIPS: { id: ResourceKindFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "checkpoint", label: "Checkpoint" },
    { id: "lora", label: "LoRA" },
    { id: "other", label: "Other" },
  ];

  const typeCounts = $derived.by(() => {
    const acc: Record<ResourceKindFilter, number> = {
      all: 0,
      checkpoint: 0,
      lora: 0,
      motion: 0,
      other: 0,
    };
    for (const m of models) {
      const kind = modelResourceKind(m);
      acc.all += 1;
      if (kind === "motion") acc.checkpoint += 1;
      else acc[kind] += 1;
    }
    return acc;
  });

  async function connect(row?: InferenceModel | null) {
    const m = row || selected;
    if (!m) {
      toast(host, "error", "Nothing selected");
      return;
    }
    if (!canConnectSelected(m)) {
      toast(
        host,
        "info",
        `${m.modality} is listed for browsing — connect is limited by this app’s modality rules.`,
      );
      return;
    }
    const access = accessFor(m);
    if (!access.allowed) {
      toast(host, "info", access.message || "Upgrade required", 9000);
      return;
    }
    if (tab === "civitai" && civitaiOffPurpose) {
      toast(
        host,
        "info",
        "CivitAI models are image and video. Use this tab in ImageForge or VideoForge.",
        8000,
      );
      return;
    }
    connecting = true;
    try {
      if (tab === "production") {
        await connectProductionModel(host, m);
      } else {
        await connectCatalogModel(host, tab, m);
      }
      connectedId = pairId(m);
      host.onNavigate?.("editor");
    } catch (e) {
      toast(host, "error", e instanceof Error ? e.message : String(e));
    } finally {
      connecting = false;
    }
  }

  async function tryPair() {
    const m = selected;
    if (!m) {
      toast(host, "error", "Nothing selected");
      return;
    }
    probing = true;
    probeResult = null;
    try {
      const result = await probeCatalogChat(client(), {
        model: pairChatId(m),
        prompt: probePrompt,
        appId: host.appId,
      });
      probeResult = result.ok
        ? { ok: true, text: result.text }
        : { ok: false, text: result.error };
      if (result.ok) {
        toast(host, "success", `Live · ${m.name}${selectedEngine ? ` · ${selectedEngine}` : ""}`);
      } else {
        toast(host, "error", result.error);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      probeResult = { ok: false, text: msg };
      toast(host, "error", msg);
    } finally {
      probing = false;
    }
  }

  function modelIsRed(m: InferenceModel): boolean {
    return m.rating === "red" || m.nsfw === true;
  }

  const greenCount = $derived(models.filter((m) => !modelIsRed(m)).length);
  const redCount = $derived(models.filter((m) => modelIsRed(m)).length);

  function persistRatingToggles() {
    try {
      localStorage.setItem("forgesuite.civitai.showGreen.v1", showGreen ? "1" : "0");
      if (allowRed) {
        localStorage.setItem("forgesuite.civitai.showRed.v1", showRed ? "1" : "0");
      }
    } catch {
      /* ignore */
    }
  }

  function countFor(t: ModalityFilter): number {
    if (t === "all") return total || models.length;
    if (t === "other") {
      const known = [
        "text-generation",
        "text-to-speech",
        "text-to-image",
        "text-to-video",
        "image-to-video",
        "image-to-image",
      ];
      return models.filter((m) => !known.includes(m.modality)).length;
    }
    if (t === "text-to-image")
      return (byModality["text-to-image"] || 0) + (byModality["image-to-image"] || 0);
    if (t === "text-to-video")
      return (byModality["text-to-video"] || 0) + (byModality["image-to-video"] || 0);
    return byModality[t] || 0;
  }
</script>

<div class="fgp-root fgp-col" data-fgp-tab={tab}>
  <div class="fgp-top">
    <h2>{meta.title}</h2>
    <span class="fgp-top-meta">
      {meta.subtitle}
      {#if purposeTotal}
        · <strong>{purposeTotal}</strong> models
      {/if}
    </span>
    <div class="fgp-top-actions">
      <span class="fgp-balance" style="color:var(--fgp-sky)"
        >{formatUsd(host.wallet.balanceUsd, 2)}</span
      >
      <button
        type="button"
        class="fgp-btn fgp-btn-ghost"
        disabled={loading}
        onclick={() => void loadCatalog(true)}
      >
        {loading ? "…" : "Refresh"}
      </button>
    </div>
  </div>

  {#if civitaiOffPurpose}
    <div class="fgp-alert">
      CivitAI is image and video models. {product} uses chat or voice here — browse the list,
      or pick HF models / Cloudflare / a Forge endpoint to generate.
    </div>
  {:else if loadError}
    <div class="fgp-alert">{loadError}</div>
  {:else if !configured && !loading}
    <div class="fgp-alert">
      {meta.title} catalog is offline.
    </div>
  {/if}

  <div class="fgp-collapse">
      <button
        type="button"
        class="fgp-collapse-btn"
        aria-expanded={catalogNoteOpen}
        onclick={toggleCatalogNote}
      >
        <span style="width:0.75rem;flex-shrink:0;font-size:10px;color:#38bdf8"
          >{catalogNoteOpen ? "▾" : "▸"}</span
        >
        <span style="min-width:0;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
          <strong style="color:#bae6fd">{meta.title}</strong>
          {#if !catalogNoteOpen}
            <span class="fgp-muted">
              · hosted catalog
              {#if total}
                <strong style="color:var(--fgp-ink-2)">{total}</strong>
              {/if}
            </span>
          {/if}
        </span>
      </button>
      {#if catalogNoteOpen}
        <div class="fgp-collapse-body">
          <p style="margin:0">
            {#if tab === "cloudflare"}
              Cloudflare lists what this suite account can call — currently
              <strong style="color:var(--fgp-ink)">{total || "…"}</strong> models.
            {:else if tab === "hf"}
              Each row is a model × engine pair. Providers rotate — lock the pair,
              Try one prompt, then Connect.
              {#if showLoraUi}
                Type chips split Checkpoint vs LoRA. Connect a checkpoint; Add LoRA stacks adapters
                (AccessHub Models → Forge HF LoRA picker).
              {/if}
            {:else}
              CivitAI catalog. Green = SFW, Red = NSFW. Type chips split Checkpoint vs LoRA.
              Connect a checkpoint; Add LoRA stacks adapters. Chat apps browse only.
            {/if}
          </p>
          {#if catalogNote}
            <p class="fgp-muted" style="margin:0;font-size:10px">{catalogNote}</p>
          {/if}
        </div>
      {/if}
    </div>

    <div class="fgp-grid">
      <div class="fgp-panel">
        <div class="fgp-panel-head">
          <input class="fgp-input" placeholder="Search models…" bind:value={q} />

          {#if modalityTabs.length}
            <div class="fgp-chip-row">
              {#each modalityTabs as t}
                <button
                  type="button"
                  class="fgp-chip"
                  data-tone={meta.tone}
                  data-active={modality === t.id}
                  onclick={() => (modality = t.id)}
                >
                  {t.label}
                  <span style="opacity:0.7">({countFor(t.id)})</span>
                </button>
              {/each}
            </div>
          {/if}

          {#if showLoraUi}
            <div class="fgp-chip-row">
              <span class="fgp-chip-label">Type</span>
              {#each TYPE_CHIPS as chip}
                <button
                  type="button"
                  class="fgp-chip"
                  data-tone={meta.tone}
                  data-active={resourceKindFilter === chip.id}
                  onclick={() => {
                    resourceKindFilter = chip.id;
                    if (tab === "civitai") {
                      civitaiCursors = [];
                      void loadCatalog();
                    }
                  }}
                >
                  {chip.label}
                  <span style="opacity:0.75">({typeCounts[chip.id]})</span>
                </button>
              {/each}
            </div>
            <div class="fgp-chip-row">
              <span class="fgp-chip-label">Rating</span>
              <button
                type="button"
                class="fgp-chip"
                data-tone="green"
                data-active={showGreen}
                onclick={() => {
                  showGreen = !showGreen;
                  persistRatingToggles();
                }}
              >
                Green
                <span style="opacity:0.75">({greenCount})</span>
              </button>
              {#if allowRed}
                <button
                  type="button"
                  class="fgp-chip"
                  data-tone="red"
                  data-active={showRed}
                  onclick={() => {
                    showRed = !showRed;
                    persistRatingToggles();
                  }}
                >
                  Red
                  <span style="opacity:0.75">({redCount})</span>
                </button>
              {/if}
            </div>
          {/if}

          <div class="fgp-chip-row">
            <span class="fgp-chip-label">Sort</span>
            {#each (tab === "civitai"
              ? [
                  ["downloads", "Downloads"],
                  ["name", "Name"],
                  ["favorites", "★"],
                ]
              : [
                  ["price-asc", "$ low"],
                  ["price-desc", "$ high"],
                  ["name", "Name"],
                  ["favorites", "★"],
                ]) as [mode, label]}
              <button
                type="button"
                class="fgp-chip"
                data-tone={meta.tone}
                data-active={sortMode === mode}
                onclick={() => (sortMode = mode as CatalogSortMode)}
              >
                {label}
              </button>
            {/each}
            <button
              type="button"
              class="fgp-chip"
              data-tone="amber"
              data-active={favoritesOnly}
              onclick={() => (favoritesOnly = !favoritesOnly)}
            >
              ★ Fav
            </button>
          </div>

          <p class="fgp-muted" style="margin:0">
            {#if loading}
              Loading {meta.title}…
            {:else if tab === "civitai"}
              Page {civitaiPage} · {filtered.length} models
              {#if modality !== "all"}
                · filter: {modality}
              {/if}
              · {showGreen ? "green on" : "green off"}
              · {showRed ? "red on" : "red off"}
            {:else}
              Showing {filtered.length} of {purposeTotal}{tab === "hf" ? " pairs" : ""}
              {#if modality !== "all"}
                · filter: {modality}
              {/if}
            {/if}
          </p>
          {#if tab === "civitai"}
            <div class="fgp-chip-row" role="navigation" aria-label="CivitAI pages">
              <button
                type="button"
                class="fgp-chip"
                data-tone={meta.tone}
                disabled={loading || civitaiCursors.length === 0}
                onclick={civitaiPrev}
              >
                Prev
              </button>
              <span class="fgp-chip-label">Page {civitaiPage}</span>
              <button
                type="button"
                class="fgp-chip"
                data-tone={meta.tone}
                data-active={Boolean(civitaiNext) && !loading}
                disabled={loading || !civitaiNext}
                onclick={civitaiNextPage}
              >
                Next
              </button>
            </div>
          {/if}
        </div>

        <div class="fgp-panel-body">
          {#each filtered as m (catalogRowKey(m))}
            {@const active = catalogRowKey(selected || {}) === catalogRowKey(m)}
            {@const badge = accessBadgeFor(m)}
            {@const engine = engineName(m.provider, tab)}
            {@const using = connectedId === pairId(m)}
            <div class="fgp-row" data-active={active} data-tone={meta.tone}>
              <button
                type="button"
                class="fgp-star"
                data-on={m.favorite}
                onclick={(e) => toggleStar(m, e)}
              >
                {m.favorite ? "★" : "☆"}
              </button>
              <button
                type="button"
                class="fgp-row-main"
                onclick={() => {
                  selectedSkuId = catalogRowKey(m);
                  probeResult = null;
                  host.onHighlightModel?.(m.chatModelId || m.modelId, {
                    name: m.name,
                    modality: m.modality,
                    provider: m.provider,
                  });
                  if (host.purpose === "chat") void connect(m);
                }}
              >
                <div style="min-width:0;flex:1">
                  <div class="fgp-row-title">
                    <span class="name">{m.name}</span>
                    {#if engine}
                      <span class="fgp-badge fgp-badge-engine">{engine}</span>
                    {/if}
                    {#if using}
                      <span class="fgp-badge fgp-badge-using">Using</span>
                    {/if}
                    {#if tab === "civitai"}
                      <span class="fgp-badge {modelIsRed(m) ? 'fgp-badge-red' : 'fgp-badge-green'}">
                        {modelIsRed(m) ? "Red" : "Green"}
                      </span>
                      <span class="fgp-badge fgp-badge-kind">{resourceKindLabel(modelResourceKind(m))}</span>
                    {/if}
                    <span class="fgp-badge-mod">{m.modality?.replace(/-/g, " ") || "model"}</span>
                    {#if modelHasImageInput(m)}
                      <span class="fgp-badge-mod">vision</span>
                    {/if}
                    {#if badge === "Member"}
                      <span class="fgp-badge fgp-badge-member">Member</span>
                    {:else if badge === "Credits"}
                      <span class="fgp-badge fgp-badge-credits">Credits</span>
                    {/if}
                  </div>
                  <div class="fgp-row-sub">{m.modelId}{engine ? ` · ${engine}` : ""}{m.creator ? ` · ${m.creator}` : ""}</div>
                </div>
                <div class="fgp-row-price {meta.tone === 'sky' ? 'sky' : ''}">
                  {#if m.offer?.pricePerHour}
                    {formatUsd(m.offer.pricePerHour)}
                  {:else}
                    —
                  {/if}
                </div>
              </button>
            </div>
          {:else}
            <p class="fgp-empty">
              {loading
                ? "Loading…"
                : !configured
                  ? `${meta.title} catalog is offline.`
                  : tab === "civitai" && !showGreen && !showRed
                    ? "Green and Red are both off. Turn one on."
                    : showLoraUi && resourceKindFilter === "lora"
                    ? "No LoRAs in this catalog slice. Refresh or search."
                    : q.trim()
                    ? "No models match that search."
                    : purposeTotal === 0
                    ? host.purpose === "video"
                      ? "No live Hugging Face video models right now. Try Cloudflare or CivitAI."
                      : host.purpose === "image"
                        ? "No live Hugging Face image models right now. Try Cloudflare or CivitAI."
                        : `No ${meta.title} models for this app.`
                    : modality !== "all" || (tab === "civitai" && (!showGreen || !showRed))
                    ? "No models in this filter. Clear search or change Green / Red."
                    : `No ${meta.title} models.`}
            </p>
          {/each}
        </div>
        {#if showLoraUi && (host.purpose === "image" || host.purpose === "video" || host.purpose === "vision")}
          <div class="fgp-lora-tray" aria-label="LoRA stack">
            <div class="fgp-lora-tray-head">
              <strong>LoRAs</strong>
              <span class="fgp-muted">{loraStack.length}/{MAX_LORAS}</span>
            </div>
            {#if !loraCapable}
              <p class="fgp-muted" style="margin:0">
                {tab === "hf"
                  ? "Pick a Hugging Face checkpoint, then Add LoRA."
                  : "Pick a CivitAI checkpoint, then Add LoRA."}
              </p>
            {:else if !loraStack.length}
              <p class="fgp-muted" style="margin:0">None attached. Filter Type → LoRA, then Add LoRA.</p>
            {:else}
              {#each loraStack as lora (loraKey(lora))}
                <div class="fgp-lora-row">
                  <div class="fgp-lora-meta">
                    <span class="name">{lora.name}</span>
                    {#if !loraMatchesEngine(lora.baseModel, host.connectedEngineBaseModel || undefined)}
                      <span class="fgp-badge fgp-badge-warn">base mismatch</span>
                    {/if}
                  </div>
                  <label class="fgp-lora-str">
                    {lora.strength.toFixed(2)}
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.05"
                      value={lora.strength}
                      oninput={(e) =>
                        weightLora(loraKey(lora), Number((e.currentTarget as HTMLInputElement).value))}
                    />
                  </label>
                  <button type="button" class="fgp-chip" onclick={() => dropLora(loraKey(lora))}>Remove</button>
                </div>
              {/each}
            {/if}
          </div>
        {/if}
      </div>

      <div class="fgp-panel">
        {#if !selected}
          <div class="fgp-empty" style="flex:1;display:flex;align-items:center;justify-content:center">
            Pick a model
          </div>
        {:else}
          <div class="fgp-panel-side">
            <div>
              <p class="fgp-muted" style="margin:0;text-transform:uppercase;letter-spacing:0.06em;font-size:11px">
                {selected.modality?.replace(/-/g, " ")}
                {#if tab === "civitai"}
                  · {modelIsRed(selected) ? "Red" : "Green"}
                {/if}
              </p>
              <h3 class="fgp-detail-title">{selected.name}</h3>
              <p class="fgp-row-sub" style="margin:0.25rem 0 0">
                {selected.modelId}{selectedEngine ? ` · ${selectedEngine}` : ""}
              </p>
              {#if selectedEngine}
                <p class="fgp-pair-label">
                  Pair · <strong>{selected.name}</strong> × <strong>{selectedEngine}</strong>
                  {#if selectedIsConnected}
                    · using
                  {/if}
                </p>
              {/if}
            </div>

            {#if selected.modality === "text-generation"}
              <div class="fgp-probe">
                <label class="fgp-probe-label" for="fgp-probe-prompt">Try this pair · one prompt</label>
                <input
                  id="fgp-probe-prompt"
                  class="fgp-input"
                  bind:value={probePrompt}
                  maxlength="500"
                />
                <button
                  type="button"
                  class="fgp-btn fgp-btn-ghost fgp-btn-block"
                  disabled={probing || connecting}
                  onclick={() => void tryPair()}
                >
                  {probing ? "Trying…" : "Try one prompt"}
                </button>
                {#if probeResult}
                  <pre
                    class="fgp-probe-out"
                    data-ok={probeResult.ok}
                  >{probeResult.ok ? probeResult.text : probeResult.text}</pre>
                {/if}
              </div>
            {/if}

            {#if tab === "civitai" && selected.previewUrl}
              <img
                class="fgp-civitai-preview"
                src={selected.previewUrl}
                alt={selected.name}
              />
            {/if}

            {#if tab === "civitai" && selected.detail}
              <div class="fgp-civitai-body">{@html selected.detail}</div>
            {/if}

            {#if selected.offer?.pricePerHour}
              <div class="fgp-price-card">
                <p class="label">List rate (Forge wallet)</p>
                <p class="big sky">
                  {formatUsd(selected.offer.pricePerHour)}
                  <span>/hr est.</span>
                </p>
              </div>
            {/if}

            {#if selected.modality === "text-generation" && workUnits.length}
              {@const avgWords = Math.round(
                workUnits.reduce((n, c) => n + (c.wordCount || 0), 0) /
                  Math.max(1, workUnits.length),
              )}
              {@const avgEst = chapterEstUsd(avgWords, Number(selected.offer?.pricePerHour) || 0)}
              <div class="fgp-price-card" style="background:color-mix(in srgb,var(--fgp-panel) 50%,transparent)">
                <p class="label">Unit estimates</p>
                <p class="sub">{host.workTitle || product} · {workUnits.length} units</p>
                {#if avgEst != null}
                  <p class="big sky" style="font-size:1.4rem">
                    ~{formatUsd(avgEst)}
                    <span>/unit avg</span>
                  </p>
                {/if}
              </div>
            {/if}

            {#if showLoraUi && selectedIsLora(selected)}
              <p class="fgp-muted" style="margin:0">
                LoRAs are adapters. Connect a Checkpoint first, then Add LoRA.
              </p>
            {:else if tab === "civitai" && civitaiHosted(selected)}
              <p class="fgp-muted" style="margin:0">
                Already loaded on a Forge endpoint — Connect uses that box.
              </p>
            {:else if tab === "civitai" && !civitaiOffPurpose}
              <p class="fgp-muted" style="margin:0">
                Connect runs this checkpoint through the CivitAI generator (suite credits).
              </p>
            {/if}
          </div>

          <div class="fgp-panel-foot">
            {#if selectedAccess && !selectedAccess.allowed && selectedAccess.badge === "Member"}
              <div class="fgp-gate">
                <strong>Member only</strong>
                — {tab === "cloudflare" ? "Cloudflare" : "This catalog"} is Suite Member
                (${host.wallet.memberPriceUsd}/mo).
                Free/credits cover open-weight sizes
                ({freeModelBandLabel()} · {creditsBandLabel()}).
              </div>
              <button
                type="button"
                class="fgp-btn fgp-btn-primary fgp-btn-block"
                disabled={host.wallet.checkoutBusy || host.wallet.isMember}
                onclick={() => host.onSubscribeMember?.()}
              >
                {host.wallet.checkoutBusy
                  ? "Opening Stripe…"
                  : host.wallet.isMember
                    ? "Member active — Refresh account"
                    : `Unlock Member · $${host.wallet.memberPriceUsd}/mo`}
              </button>
            {:else if selectedAccess && !selectedAccess.allowed && selectedAccess.badge === "Credits"}
              <div class="fgp-gate sky">
                <strong>Credits required</strong> for this mid-size model.
              </div>
              <button
                type="button"
                class="fgp-btn fgp-btn-secondary fgp-btn-block"
                disabled={host.wallet.checkoutBusy}
                onclick={() => host.onBuyPack?.(5)}
              >
                Buy $5 credits
              </button>
              <button
                type="button"
                class="fgp-btn fgp-btn-primary fgp-btn-block"
                disabled={host.wallet.checkoutBusy}
                onclick={() => host.onSubscribeMember?.()}
              >
                Or Member · ${host.wallet.memberPriceUsd}/mo
              </button>
            {:else if selectedIsLora(selected)}
              <button
                type="button"
                class="fgp-btn fgp-btn-primary fgp-btn-block"
                disabled={!loraCapable || loraStack.length >= MAX_LORAS}
                onclick={() => addSelectedLora()}
              >
                {!loraCapable
                  ? tab === "hf"
                    ? "Need an HF checkpoint first"
                    : "Need a CivitAI checkpoint first"
                  : loraStack.length >= MAX_LORAS
                    ? `LoRA stack full (${MAX_LORAS})`
                    : "Add LoRA"}
              </button>
            {:else}
              <button
                type="button"
                class="fgp-btn fgp-btn-primary fgp-btn-block"
                disabled={connecting || host.connecting || !canConnectSelected(selected)}
                onclick={() => void connect()}
              >
                {connecting || host.connecting
                  ? "Connecting…"
                  : canConnectSelected(selected)
                    ? ctaLabel
                    : "Not available for this modality"}
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>
</div>
